'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Star, MapPin, CalendarDays, Globe, Loader2, RefreshCw, Navigation, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';

// Heart icon for Donate button
function HeartDonateIcon({ size = 16, className = '' }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
  );
}

// ── Lightbox ──────────────────────────────────────────────────────────────
function Lightbox({ photos, startIndex, onClose }: { photos: string[]; startIndex: number; onClose: () => void }) {
  const [idx, setIdx] = useState(startIndex);
  const prev = () => setIdx((i) => Math.max(0, i - 1));
  const next = () => setIdx((i) => Math.min(photos.length - 1, i + 1));

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center" onClick={onClose}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center z-10 transition-colors"
      >
        <X size={20} />
      </button>
      {photos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-sm px-3 py-1 rounded-full z-10">
          {idx + 1} / {photos.length}
        </div>
      )}
      {idx > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center z-10 transition-colors"
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {idx < photos.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center z-10 transition-colors"
        >
          <ChevronRight size={22} />
        </button>
      )}
      <img
        src={photos[idx]}
        alt={`Photo ${idx + 1}`}
        className="max-w-full max-h-full object-contain select-none"
        style={{ maxHeight: '90vh', maxWidth: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ── Truncated Caption ─────────────────────────────────────────────────────
const CAPTION_LIMIT = 180;
function TruncatedCaption({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  if (text.length <= CAPTION_LIMIT) {
    return <p className="text-sm text-[#1c1e21] leading-relaxed whitespace-pre-wrap">{text}</p>;
  }
  return (
    <p className="text-sm text-[#1c1e21] leading-relaxed whitespace-pre-wrap">
      {expanded ? text : `${text.slice(0, CAPTION_LIMIT)}…`}
      {' '}
      <button
        onClick={() => setExpanded((v) => !v)}
        className="text-[#2d7a4f] font-semibold hover:underline focus:outline-none"
      >
        {expanded ? 'Itago' : 'Basahin pa'}
      </button>
    </p>
  );
}

interface FeedPost {
  id: string;
  pantryId: string;
  pantryName: string;
  pantryPhotoUrl?: string;
  pantryLungsod?: string;
  pantryBarangay?: string;
  isAdmin?: boolean;
  type: 'post' | 'event';
  content?: string;
  photoUrls?: string[];
  photoUrl?: string;
  eventName?: string;
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  details?: string;
  isRepeating?: boolean;
  frequency?: string;
  customFrequency?: string;
  eventLocation?: string;
  eventLat?: number;
  eventLng?: number;
  createdAt: string;
}

function formatPostDate(iso: string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'Kakapost lang';
  if (diffMins < 60) return `${diffMins}m ang nakalipas`;
  if (diffHours < 24) return `${diffHours}h ang nakalipas`;
  if (diffDays < 7) return `${diffDays}d ang nakalipas`;
  return d.toLocaleDateString('fil-PH', { month: 'short', day: 'numeric', year: 'numeric' });
}

function PostPhotoCarousel({ photos, onPhotoClick }: { photos: string[]; onPhotoClick: (index: number) => void }) {
  const [current, setCurrent] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollTo = useCallback((index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const child = container.children[index] as HTMLElement;
    if (child) {
      container.scrollTo({ left: child.offsetLeft, behavior: 'smooth' });
    }
    setCurrent(index);
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const width = container.offsetWidth;
    const newIndex = Math.round(scrollLeft / width);
    setCurrent(newIndex);
  }, []);

  if (photos.length === 0) return null;
  if (photos.length === 1) {
    return (
      <div className="w-full bg-[#f0f2f5] overflow-hidden cursor-pointer" onClick={() => onPhotoClick(0)}>
        <img src={photos[0]} alt="Post photo" className="w-full max-h-80 object-cover hover:opacity-95 transition-opacity" />
      </div>
    );
  }
  return (
    <div className="relative w-full bg-[#f0f2f5]">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: 'x mandatory' }}
      >
        {photos.map((photo, i) => (
          <div
            key={i}
            className="flex-shrink-0 w-full snap-center cursor-pointer"
            style={{ scrollSnapAlign: 'center' }}
            onClick={() => onPhotoClick(i)}
          >
            <img
              src={photo}
              alt={`Post photo ${i + 1}`}
              className="w-full max-h-80 object-cover hover:opacity-95 transition-opacity"
            />
          </div>
        ))}
      </div>
      {current > 0 && (
        <button
          type="button"
          onClick={() => scrollTo(current - 1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center z-10"
        >
          ‹
        </button>
      )}
      {current < photos.length - 1 && (
        <button
          type="button"
          onClick={() => scrollTo(current + 1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 hover:bg-black/60 text-white rounded-full flex items-center justify-center z-10"
        >
          ›
        </button>
      )}
      <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 z-10">
        {photos.map((_, i) => (
          <button key={i} type="button" onClick={() => scrollTo(i)} className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white' : 'bg-white/50'}`} />
        ))}
      </div>
      <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full z-10">{current + 1}/{photos.length}</div>
    </div>
  );
}

interface FeedViewProps {
  onDonate: (pantryId?: string) => void;
}

export default function FeedView({ onDonate }: FeedViewProps) {
  const [feedItems, setFeedItems] = useState<FeedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState<Record<string, boolean>>({});
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [lightbox, setLightbox] = useState<{ photos: string[]; index: number } | null>(null);

  useEffect(() => {
    loadFeed();
  }, []);

  const loadFeed = async () => {
    setLoading(true);
    const supabase = createClient();

    // Fetch pantries map
    const { data: pantriesData } = await supabase
      .from('pantries')
      .select('id, pangalan, photo_url, lungsod, barangay');
    const pantryMap: Record<string, { pangalan: string; photo_url?: string; lungsod?: string; barangay?: string }> = {};
    (pantriesData ?? []).forEach((p: Record<string, unknown>) => {
      pantryMap[p.id as string] = {
        pangalan: p.pangalan as string,
        photo_url: p.photo_url as string | undefined,
        lungsod: p.lungsod as string | undefined,
        barangay: p.barangay as string | undefined,
      };
    });

    // Fetch pantry posts
    const { data: postsData } = await supabase
      .from('pantry_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    // Fetch pantry events
    const { data: eventsData } = await supabase
      .from('pantry_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    // Fetch admin posts
    const { data: adminPostsData } = await supabase
      .from('admin_posts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    // Fetch admin events
    const { data: adminEventsData } = await supabase
      .from('admin_events')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(20);

    const posts: FeedPost[] = (postsData ?? []).map((row: Record<string, unknown>) => {
      const pantry = pantryMap[row.pantry_id as string] ?? { pangalan: 'Unknown Pantry' };
      const photoUrls = (row.photo_urls as string[] | null) ?? [];
      const singlePhotoUrl = (row.photo_url as string | null) ?? undefined;
      const allPhotos = photoUrls.length > 0 ? photoUrls : (singlePhotoUrl ? [singlePhotoUrl] : []);
      return {
        id: row.id as string,
        pantryId: row.pantry_id as string,
        pantryName: pantry.pangalan,
        pantryPhotoUrl: pantry.photo_url,
        pantryLungsod: pantry.lungsod,
        pantryBarangay: pantry.barangay,
        type: 'post',
        content: row.content as string | undefined,
        photoUrls: allPhotos,
        createdAt: row.created_at as string,
      };
    }).filter((p) => pantryMap[p.pantryId] !== undefined);

    const events: FeedPost[] = (eventsData ?? []).map((row: Record<string, unknown>) => {
      const pantry = pantryMap[row.pantry_id as string] ?? { pangalan: 'Unknown Pantry' };
      return {
        id: row.id as string,
        pantryId: row.pantry_id as string,
        pantryName: pantry.pangalan,
        pantryPhotoUrl: pantry.photo_url,
        pantryLungsod: pantry.lungsod,
        pantryBarangay: pantry.barangay,
        type: 'event',
        eventName: row.event_name as string,
        startDate: row.start_date as string,
        startTime: row.start_time as string | undefined,
        endDate: row.end_date as string | undefined,
        endTime: row.end_time as string | undefined,
        details: row.details as string | undefined,
        photoUrl: (row.photo_url as string | null) ?? undefined,
        isRepeating: row.is_repeating as boolean,
        frequency: row.frequency as string | undefined,
        customFrequency: row.custom_frequency as string | undefined,
        eventLocation: (row.event_location as string | null) ?? undefined,
        eventLat: (row.event_lat as number | null) ?? undefined,
        eventLng: (row.event_lng as number | null) ?? undefined,
        createdAt: row.created_at as string,
      };
    }).filter((e) => pantryMap[e.pantryId] !== undefined);

    // Admin posts
    const adminPosts: FeedPost[] = (adminPostsData ?? []).map((row: Record<string, unknown>) => {
      const photoUrls = (row.photo_urls as string[] | null) ?? [];
      const singlePhotoUrl = (row.photo_url as string | null) ?? undefined;
      const allPhotos = photoUrls.length > 0 ? photoUrls : (singlePhotoUrl ? [singlePhotoUrl] : []);
      return {
        id: `admin-post-${row.id as string}`,
        pantryId: 'admin',
        pantryName: 'TulongTayo Admin',
        isAdmin: true,
        type: 'post',
        content: row.content as string | undefined,
        photoUrls: allPhotos,
        createdAt: row.created_at as string,
      };
    });

    // Admin events
    const adminEvents: FeedPost[] = (adminEventsData ?? []).map((row: Record<string, unknown>) => ({
      id: `admin-event-${row.id as string}`,
      pantryId: 'admin',
      pantryName: 'TulongTayo Admin',
      isAdmin: true,
      type: 'event',
      eventName: row.event_name as string,
      startDate: row.start_date as string,
      startTime: row.start_time as string | undefined,
      endDate: row.end_date as string | undefined,
      endTime: row.end_time as string | undefined,
      details: row.details as string | undefined,
      photoUrl: (row.photo_url as string | null) ?? undefined,
      isRepeating: row.is_repeating as boolean,
      frequency: row.frequency as string | undefined,
      customFrequency: row.custom_frequency as string | undefined,
      eventLocation: (row.event_location as string | null) ?? undefined,
      eventLat: (row.event_lat as number | null) ?? undefined,
      eventLng: (row.event_lng as number | null) ?? undefined,
      createdAt: row.created_at as string,
    }));

    // Merge and sort by createdAt
    const merged = [...posts, ...events, ...adminPosts, ...adminEvents].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    setFeedItems(merged);
    setLoading(false);
  };

  const toggleLike = (id: string) => {
    setLikedPosts((prev) => {
      const wasLiked = !!prev[id];
      setLikeCounts((counts) => ({
        ...counts,
        [id]: Math.max(0, (counts[id] ?? 0) + (wasLiked ? -1 : 1)),
      }));
      return { ...prev, [id]: !wasLiked };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-[#f0faf4]">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-[#2d7a4f] mx-auto mb-3" />
          <p className="text-sm text-[#2d7a4f] font-medium">Loading updates…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-[#f0f2f5]">
      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#d1ead9] px-4 py-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-[#1e5c38]">Updates</p>
          <p className="text-xs text-[#9ca3af]">Pinakabagong posts at events ng mga pantry</p>
        </div>
        <button
          onClick={loadFeed}
          className="p-2 text-[#2d7a4f] hover:bg-[#e8f5ee] rounded-lg transition-colors"
          title="I-refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {feedItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <Globe size={32} className="text-[#d1ead9] mb-3" />
          <p className="text-sm font-semibold text-[#374151] mb-1">Wala pang posts</p>
          <p className="text-xs text-[#9ca3af]">Mag-abang sa mga bagong update mula sa mga pantry.</p>
        </div>
      ) : (
        <div className="space-y-3 p-3">
          {feedItems.map((item) => (
            <div key={`${item.type}-${item.id}`} className="bg-white rounded-2xl shadow-sm overflow-hidden">
              {/* Post header */}
              <div className="px-4 pt-4 pb-3 flex items-start gap-3">
                {item.isAdmin ? (
                  <Link href="/admin-official">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden bg-[#1e5c38] cursor-pointer hover:opacity-80 transition-opacity">
                      <span className="text-white font-bold text-sm">T</span>
                    </div>
                  </Link>
                ) : (
                  <Link href={`/pantry/${item.pantryId}`}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                      style={{ background: item.type === 'event' ? '#7c3aed' : '#2d7a4f' }}>
                      {item.pantryPhotoUrl ? (
                        <img src={item.pantryPhotoUrl} alt={item.pantryName} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-white font-bold text-sm">{item.pantryName.charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                  </Link>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {item.isAdmin ? (
                      <Link href="/admin-official" className="text-sm font-bold text-[#1e5c38] hover:underline">
                        {item.pantryName}
                      </Link>
                    ) : (
                      <Link href={`/pantry/${item.pantryId}`} className="text-sm font-bold text-[#1e5c38] hover:underline">
                        {item.pantryName}
                      </Link>
                    )}
                    {item.type === 'event' && (
                      <span className="px-2 py-0.5 bg-[#f5f3ff] text-[#7c3aed] text-xs font-semibold rounded-full border border-[#ddd6fe]">Event</span>
                    )}
                    {item.isAdmin && (
                      <span className="px-2 py-0.5 bg-[#e8f5ee] text-[#1e5c38] text-xs font-semibold rounded-full border border-[#bbf7d0]">Official</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[#65676b]">
                    {!item.isAdmin && item.pantryBarangay && item.pantryLungsod && (
                      <>
                        <MapPin size={10} className="text-[#2d7a4f]" />
                        <span>{item.pantryBarangay}, {item.pantryLungsod}</span>
                        <span>·</span>
                      </>
                    )}
                    <span>{formatPostDate(item.createdAt)}</span>
                    <span>·</span>
                    <Globe size={10} />
                  </div>
                </div>
              </div>

              {/* Event card */}
              {item.type === 'event' && (
                <>
                  {item.photoUrl && (
                    <div
                      className="w-full bg-[#f0f2f5] cursor-pointer"
                      onClick={() => setLightbox({ photos: [item.photoUrl!], index: 0 })}
                    >
                      <img src={item.photoUrl} alt={`${item.eventName} poster`} className="w-full max-h-80 object-cover hover:opacity-95 transition-opacity" />
                    </div>
                  )}
                  <div className="px-4 pb-3 space-y-2">
                    <p className="text-base font-bold text-[#1e5c38]">{item.eventName}</p>
                    <div className="flex flex-wrap gap-2 text-xs text-[#6b7280]">
                      <span className="flex items-center gap-1 bg-[#f0faf4] px-2 py-1 rounded-lg">
                        <CalendarDays size={11} className="text-[#2d7a4f]" />
                        {item.startDate ? new Date(item.startDate + 'T00:00:00').toLocaleDateString('fil-PH', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                        {item.startTime && ` · ${item.startTime}`}
                      </span>
                      {(item.endDate || item.endTime) && (
                        <span className="flex items-center gap-1 bg-[#f0faf4] px-2 py-1 rounded-lg">
                          hanggang {item.endDate ? new Date(item.endDate + 'T00:00:00').toLocaleDateString('fil-PH', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
                          {item.endTime && ` · ${item.endTime}`}
                        </span>
                      )}
                      {item.isRepeating && item.frequency && (
                        <span className="flex items-center gap-1 bg-[#f5f3ff] text-[#7c3aed] px-2 py-1 rounded-lg border border-[#ddd6fe]">
                          🔁 {item.frequency === 'daily' ? 'Araw-araw' : item.frequency === 'weekly' ? 'Lingguhán' : item.frequency === 'monthly' ? 'Buwanán' : item.customFrequency || 'Custom'}
                        </span>
                      )}
                      {item.eventLocation && (
                        <span className="flex items-center gap-1 bg-[#fff7ed] text-[#c2410c] px-2 py-1 rounded-lg">
                          <MapPin size={11} className="text-[#c2410c]" />
                          {item.eventLocation}
                        </span>
                      )}
                    </div>
                    {item.details && <TruncatedCaption text={item.details} />}
                    {/* Go to Location button */}
                    {(item.eventLat && item.eventLng) && (
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${item.eventLat},${item.eventLng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#2d7a4f] hover:bg-[#1e5c38] text-white text-xs font-semibold rounded-lg transition-colors"
                      >
                        <Navigation size={12} />
                        Go to Location
                      </a>
                    )}
                  </div>
                </>
              )}

              {/* Post content */}
              {item.type === 'post' && item.content && item.content !== '📸' && (
                <div className="px-4 pb-3">
                  <TruncatedCaption text={item.content} />
                </div>
              )}

              {/* Post photos — scrollable carousel */}
              {item.type === 'post' && item.photoUrls && item.photoUrls.length > 0 && (
                <PostPhotoCarousel
                  photos={item.photoUrls}
                  onPhotoClick={(index) => setLightbox({ photos: item.photoUrls!, index })}
                />
              )}

              {/* Actions */}
              <div className="px-4 py-2 border-t border-[#e5e7eb] flex gap-1">
                {/* Like button — Star icon, Twitter-style inline count */}
                <button
                  onClick={() => toggleLike(item.id)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${
                    likedPosts[item.id]
                      ? 'text-[#f59e0b] bg-[#fffbeb] hover:bg-[#fef3c7]'
                      : 'text-[#65676b] hover:bg-[#f0f2f5]'
                  }`}
                >
                  <Star
                    size={16}
                    fill={likedPosts[item.id] ? '#f59e0b' : 'none'}
                    className={likedPosts[item.id] ? 'text-[#f59e0b]' : 'text-[#65676b]'}
                  />
                  <span className="text-sm min-w-[1ch]">{likeCounts[item.id] ?? 0}</span>
                </button>
                {/* No donate button for admin posts */}
                {!item.isAdmin && (
                  <button
                    onClick={() => onDonate(item.pantryId)}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg text-[#e11d48] hover:bg-[#fff1f2] transition-colors"
                  >
                    <HeartDonateIcon size={16} className="text-[#e11d48]" />
                    Donate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
